import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, Mail, ArrowRight, ArrowLeft } from 'lucide-react'
import UniversalHeader from '@/components/shared/UniversalHeader'
import UniversalFooter from '@/components/shared/UniversalFooter'
import { useAuth } from '@/context/auth-context'
import { useToast } from '@/hooks/use-toast'


// Mobile-friendly overrides
const mobileStyles = `
  @media (max-width: 640px) {
    input[type="email"], input[type="password"] {
      font-size: 16px !important;
    }
    input:focus {
      font-size: 16px !important;
    }
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = mobileStyles;
  document.head.appendChild(style);
}

const LoginPage: React.FC = () => {
  const [step, setStep] = useState<'email' | 'password'>('email')
  const [email, setEmail] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('careVaultSavedEmail') || ''
    }
    return ''
  })
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [emailError, setEmailError] = useState('')

  const { login, user } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) {
      navigate('/app/dashboard')
    }
  }, [user, navigate])

  useEffect(() => {
    if (email && step === 'email') {
      // Clear error when user types
      setEmailError('')
    }
  }, [email, step])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value
    setEmail(newEmail)
    setEmailError('')
    if (typeof window !== 'undefined') {
      localStorage.setItem('careVaultSavedEmail', newEmail)
    }
  }

  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      setEmailError('Please enter your email address')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address')
      return
    }
    setStep('password')
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await login(email, password)
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in to CareVault.",
      })
      navigate('/app/dashboard')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Please check your credentials and try again."
      const errorCode = error.response?.data?.code

      let toastTitle = "Sign in failed"
      let toastDescription = errorMessage

      if (errorCode === 'EMAIL_NOT_CONFIRMED' ||
        errorMessage.toLowerCase().includes('confirm your email') ||
        errorMessage.toLowerCase().includes('email not confirmed')) {
        toastTitle = "Email not confirmed"
        toastDescription = "Please check your inbox and spam folder for the confirmation email."
      }

      toast({
        title: toastTitle,
        description: toastDescription,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }



  return (
    <div className="min-h-screen flex flex-col bg-white overflow-hidden">
      <UniversalHeader title="CareVault" />

      <div className="flex-1 container mx-auto px-4 pt-24 pb-8 lg:pt-32 lg:pb-12 flex items-center justify-center">
        <div className="w-full flex flex-col lg:flex-row bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
          {/* Left Side - Brand & Visuals (The "Colored" Section) */}
          <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center p-12 text-white bg-[#0F172A] min-h-[600px]">
            {/* Realistic Image Background */}
            <div className="absolute inset-0">
              <img
                src="/assets/images/hero_image.png"
                alt="Healthcare Professional"
                className="w-full h-full object-cover opacity-40 mix-blend-overlay"
              />
              {/* Navy Blue Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A] opacity-90" />
            </div>

            <div className="absolute inset-0 bg-[url('/assets/pattern.svg')] opacity-10 mix-blend-overlay" />

            <div className="relative z-10 max-w-lg text-center space-y-6">
              {/* Logo Container - White for visibility */}
              <div className="inline-flex items-center justify-center w-28 h-28 rounded-3xl bg-white shadow-2xl mb-8 border-4 border-white/20 animate-float">
                <img src="/assets/images/carevault_logo_modern.png" alt="CareVault Logo" className="w-20 h-20 object-contain" />
              </div>
              <h2 className="text-4xl font-bold tracking-tight shadow-sm text-white">Your Health, <br />Secured & Simplified.</h2>
              <p className="text-lg text-blue-100 font-medium leading-relaxed drop-shadow-md">
                Access your medical history, manage prescriptions, and connect with doctors—all from one secure vault.
              </p>
            </div>
          </div>

          {/* Right Side - Login Form (The "White" Section) */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 relative bg-white">
            <div className="w-full max-w-[420px] space-y-8">
              <div className="text-center lg:text-left mb-8">
                <h1 className="text-3xl font-bold text-[#1F303B] mb-3">
                  {step === 'email' ? 'Welcome Back' : 'Enter Password'}
                </h1>
                <p className="text-gray-500 text-base">
                  {step === 'email'
                    ? 'Sign in to access your health vault'
                    : `Signing in as ${email}`}
                </p>
              </div>

              <form onSubmit={step === 'email' ? handleNextStep : handleLogin} className="space-y-6">
                <div className="space-y-5">
                  {step === 'email' ? (
                    <div className="animate-slide-in-right space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">
                          Email Address
                        </label>
                        <div className="relative group">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#D64463] transition-colors" />
                          <input
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            className={`w-full bg-gray-50 border-2 ${emailError ? 'border-red-500' : 'border-gray-100'} focus:bg-white focus:border-[#D64463] rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400 shadow-sm`}
                            placeholder="name@example.com"
                            autoFocus
                          />
                        </div>
                        {emailError && (
                          <p className="text-red-500 text-xs mt-2 ml-1 font-medium">{emailError}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="animate-slide-in-right space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">
                          Password
                        </label>
                        <div className="relative group">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-gray-50 border-2 border-gray-100 focus:bg-white focus:border-[#D64463] rounded-2xl py-4 pl-4 pr-12 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400 shadow-sm"
                            placeholder="Enter your password"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-2">
                        <button
                          type="button"
                          onClick={() => setStep('email')}
                          className="text-sm text-gray-500 hover:text-[#D64463] transition-colors flex items-center gap-1.5 font-medium"
                        >
                          <ArrowLeft size={16} /> Change email
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="text-sm font-semibold text-[#D64463] hover:text-[#9C27B0] transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full h-14 bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:opacity-90 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-[#D64463]/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing in...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {step === 'email' ? 'Continue' : 'Sign In'}
                        {step === 'email' && <ArrowRight size={20} />}
                      </span>
                    )}
                  </Button>
                </div>

                {step === 'email' && (
                  <div className="text-center pt-2">
                    <p className="text-gray-500 text-sm">
                      Don't have an account?{' '}
                      <Link to="/register" className="font-semibold text-[#D64463] hover:text-[#9C27B0] transition-colors">
                        Create account
                      </Link>
                    </p>
                  </div>
                )}
                
                {/* Provider Login Option */}
                {step === 'email' && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-sm">Healthcare Provider?</h3>
                          <p className="text-gray-500 text-xs mt-0.5">Register your institution to access patient-shared records</p>
                          <Link 
                            to="/register" 
                            className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            Register Institution
                            <ArrowRight size={14} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#0F172A] w-full mt-auto">
        <UniversalFooter />
      </div>
    </div>
  )
}

export default LoginPage